import { __awaiter } from "tslib";
import { logError } from '@office-start/telemetry-service';
export const AppId = '0ec893e0-5785-4de6-99da-4ed124e5296c';
export var KnownAuthorities;
(function (KnownAuthorities) {
    KnownAuthorities["MSA"] = "consumers";
    KnownAuthorities["AAD"] = "organizations";
})(KnownAuthorities || (KnownAuthorities = {}));
var LocalSettingsKeys;
(function (LocalSettingsKeys) {
    LocalSettingsKeys["CurrentUserProviderId"] = "CurrentUserProviderId";
    LocalSettingsKeys["CurrentAuthority"] = "CurrentAuthority";
    LocalSettingsKeys["CurrentAccountId"] = "CurrentAccountId";
})(LocalSettingsKeys || (LocalSettingsKeys = {}));
var ProviderIds;
(function (ProviderIds) {
    ProviderIds["Local"] = "https://login.windows.local";
    ProviderIds["MsaAad"] = "https://login.microsoft.com";
})(ProviderIds || (ProviderIds = {}));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isWebView = window.chrome && window.chrome.webview;
const WebCoreManager = (() => {
    var _a, _b, _c, _d, _e;
    return isWebView
        ? (_e = (_d = (_c = (_b = (_a = window.Windows) === null || _a === void 0 ? void 0 : _a.Security) === null || _b === void 0 ? void 0 : _b.Authentication) === null || _c === void 0 ? void 0 : _c.Web) === null || _d === void 0 ? void 0 : _d.Core) === null || _e === void 0 ? void 0 : _e.WebAuthenticationCoreManager
        : null;
})();
const LocalSettings = (() => {
    var _a, _b, _c, _d;
    return isWebView
        ? (_d = (_c = (_b = (_a = window.Windows) === null || _a === void 0 ? void 0 : _a.Storage) === null || _b === void 0 ? void 0 : _b.ApplicationData) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.localSettings.values
        : null;
})();
export const isWindowsAuthAvailable = (() => !!(WebCoreManager && LocalSettings))();
// eslint-disable-next-line max-params
export function getWamAccessToken(resource, user, allowUserInteraction = false, scope, nestedClientParams) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!isWindowsAuthAvailable) {
            return Promise.reject('WebCoreManager or LocalSettings not defined');
        }
        const provider = yield WindowsTokens.getAccountProvider(user);
        if (!provider) {
            return Promise.reject('unable to get WebAccountProvider');
        }
        const account = yield WindowsTokens.getWebAccount(provider);
        if (!account || !isUsersWebAccount(user, account)) {
            return Promise.reject(`unable to get WebAccount with provider: ${provider.id}`);
        }
        return WindowsTokens.getToken(provider, account, resource, allowUserInteraction, scope, nestedClientParams);
    });
}
class WindowsTokens {
    static saveProvider(provider) {
        if (!LocalSettings)
            return;
        if (!provider.id || !provider.authority) {
            logError({
                eventName: 'HWA_Auth',
                errorType: 'Data_Error',
                message: 'Provider missing id or authority',
            });
        }
        LocalSettings.insert(LocalSettingsKeys.CurrentUserProviderId, provider.id);
        LocalSettings.insert(LocalSettingsKeys.CurrentAuthority, provider.authority);
    }
    static saveWebAccount(account) {
        if (!LocalSettings)
            return;
        WindowsTokens.currentUserAccount = account;
        const provider = account.webAccountProvider;
        if (account && !provider) {
            logError({
                eventName: 'HWA_Auth',
                errorType: 'Data_Error',
                message: 'Account missing provider',
            });
            return;
        }
        account
            ? LocalSettings.insert(LocalSettingsKeys.CurrentAccountId, account.id)
            : LocalSettings.remove(LocalSettingsKeys.CurrentAccountId);
        WindowsTokens.saveProvider(provider);
    }
    static createWebTokenRequest(provider, resource) {
        const isCommercial = provider.authority === KnownAuthorities.AAD;
        const scope = isCommercial ? 'openid' : resource || 'wl.basic';
        const clientId = isCommercial ? AppId : 'none';
        const request = new Windows.Security.Authentication.Web.Core.WebTokenRequest(provider, scope, clientId);
        if (isCommercial && resource) {
            request.properties.insert('resource', resource);
        }
        if (!request) {
            logError({
                eventName: 'HWA_Auth',
                errorType: 'Request_Failed',
                message: 'Failed to create WebTokenRequest',
            });
        }
        return request;
    }
    static findAccount(provider, id) {
        return __awaiter(this, void 0, void 0, function* () {
            let account;
            if (!WebCoreManager || !provider || !(id === null || id === void 0 ? void 0 : id.length))
                return account;
            try {
                account = yield WebCoreManager.findAccountAsync(provider, id);
            }
            catch (_a) { }
            return account;
        });
    }
    static isUsersAccountProvider(provider, user) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            if (!WebCoreManager || !provider || !((_a = user === null || user === void 0 ? void 0 : user.profile) === null || _a === void 0 ? void 0 : _a.oid))
                return;
            // user.profile has an oid (objectId) but that's not what WAM uses as the accountId so this might fail
            const account = yield WindowsTokens.findAccount(provider, user.profile.oid);
            if (account)
                return account;
            // Get all the accounts for the provider and check for the matching 'OID' property
            // on the account but if the findAllAccountsAsync method isn't supported
            // there's nothing more that can be done
            if (!WebCoreManager.findAllAccountsAsync ||
                !((_e = (_d = (_c = (_b = Windows.Foundation) === null || _b === void 0 ? void 0 : _b.Metadata) === null || _c === void 0 ? void 0 : _c.ApiInformation) === null || _d === void 0 ? void 0 : _d.isMethodPresent) === null || _e === void 0 ? void 0 : _e.call(_d, 'Windows.Security.Authentication.Web.Core.WebAuthenticationCoreManager', 'FindAllAccountsAsync'))) {
                return;
            }
            const result = yield WebCoreManager.findAllAccountsAsync(provider, AppId);
            for (const account of result.accounts) {
                if (isUsersWebAccount(user, account))
                    return account;
            }
            return;
        });
    }
    static getAccountProviderForId(providerId = ProviderIds.Local, authority = null) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!WebCoreManager)
                return;
            let provider = yield (authority
                ? WebCoreManager.findAccountProviderAsync(providerId, authority)
                : WebCoreManager.findAccountProviderAsync(providerId));
            // If the returned provider is 'local' get the corresponding AAD provider instead. loosely based on:
            // https://office.visualstudio.com/OC/_git/OfficeHome?path=%2Fservices%2FOfficeHome%2FOfficeHome.ClientApp%2Fts%2Fpwa%2Foffice-pwa.ts&_a=contents&version=GBmaster
            if ((provider === null || provider === void 0 ? void 0 : provider.id) === ProviderIds.Local) {
                provider = yield (WebCoreManager === null || WebCoreManager === void 0 ? void 0 : WebCoreManager.findAccountProviderAsync(ProviderIds.MsaAad, provider.authority));
            }
            return provider;
        });
    }
    static getAccountProvider(user = null) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!WebCoreManager)
                return;
            // if the cached webAccount matches the user return the associated provider
            if (((_a = WindowsTokens.currentUserAccount) === null || _a === void 0 ? void 0 : _a.webAccountProvider) &&
                isUsersWebAccount(user, WindowsTokens.currentUserAccount)) {
                return (_b = WindowsTokens.currentUserAccount) === null || _b === void 0 ? void 0 : _b.webAccountProvider;
            }
            // Otherwise, find the one from local settings if present
            // or the local provider
            let authority = LocalSettings === null || LocalSettings === void 0 ? void 0 : LocalSettings[LocalSettingsKeys.CurrentAuthority];
            let providerId = (LocalSettings === null || LocalSettings === void 0 ? void 0 : LocalSettings[LocalSettingsKeys.CurrentUserProviderId]) ||
                ProviderIds.Local;
            // As the local provider is only used to determine the authority before retrieving
            // the MsaAad provider with the authority, there is really only one provider with
            // two options for authority to locate the correct provider for the user, so first
            // try the stored authority, then the other to find the associated provider/authority
            let authorities = [KnownAuthorities.AAD, KnownAuthorities.MSA];
            let provider;
            while ((authority === null || authority === void 0 ? void 0 : authority.length) || authorities.length) {
                if (authority === null || authority === void 0 ? void 0 : authority.length) {
                    providerId = ProviderIds.MsaAad;
                }
                provider = yield WindowsTokens.getAccountProviderForId(providerId, authority);
                // If not trying to match a provider with a user then we're done
                if (provider && !user)
                    return provider;
                // otherwise, return the provider if it is for the supplied user
                const account = yield WindowsTokens.isUsersAccountProvider(provider, user);
                if (account) {
                    WindowsTokens.saveWebAccount(account);
                    return provider;
                }
                if (provider) {
                    authorities = authorities.filter((a) => a != (provider === null || provider === void 0 ? void 0 : provider.authority));
                }
                authority = authorities.shift();
            }
            return;
        });
    }
    static getWebAccount(provider, accountId = null) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (!WebCoreManager)
                return;
            if (!accountId) {
                accountId = LocalSettings === null || LocalSettings === void 0 ? void 0 : LocalSettings[LocalSettingsKeys.CurrentAccountId];
            }
            // if the cached webAccount matches the user return it
            if (((_a = WindowsTokens.currentUserAccount) === null || _a === void 0 ? void 0 : _a.id) === accountId)
                return WindowsTokens.currentUserAccount;
            if (!provider) {
                return;
            }
            let account;
            if (accountId) {
                account = yield WindowsTokens.findAccount(provider, accountId);
                if (account) {
                    return account;
                }
            }
            const tokenRequest = WindowsTokens.createWebTokenRequest(provider);
            const tokenResult = yield WebCoreManager.getTokenSilentlyAsync(tokenRequest);
            if ((tokenResult === null || tokenResult === void 0 ? void 0 : tokenResult.responseStatus) !==
                Windows.Security.Authentication.Web.Core.WebTokenRequestStatus
                    .success) {
                logError({
                    eventName: 'HWA_Auth',
                    errorType: 'Authentication_Failed',
                    message: ((_b = tokenResult === null || tokenResult === void 0 ? void 0 : tokenResult.responseError) === null || _b === void 0 ? void 0 : _b.errorMessage) ||
                        ((_c = tokenResult === null || tokenResult === void 0 ? void 0 : tokenResult.responseStatus) === null || _c === void 0 ? void 0 : _c.toString()),
                });
                return account;
            }
            account = (_d = tokenResult === null || tokenResult === void 0 ? void 0 : tokenResult.responseData) === null || _d === void 0 ? void 0 : _d[0].webAccount;
            WindowsTokens.saveWebAccount(account);
            return account;
        });
    }
    // eslint-disable-next-line max-params
    static getToken(provider, account, resource, allowUserInteraction, scope, nestedClientParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const tokenRequest = WindowsTokens.createWebTokenRequest(provider, resource);
            if (nestedClientParams) {
                // inject nested client params
                tokenRequest.properties.insert('child_client_id', nestedClientParams.clientId);
                tokenRequest.properties.insert('child_redirect_uri', nestedClientParams.redirectUri);
                tokenRequest.scope = getEffectiveScope(scope);
                tokenRequest.properties.insert('wam_compat', '2.0');
            }
            else {
                // No op: nested client params are skipped
            }
            const fetchSilent = () => __awaiter(this, void 0, void 0, function* () {
                return yield (account
                    ? WebCoreManager === null || WebCoreManager === void 0 ? void 0 : WebCoreManager.getTokenSilentlyAsync(tokenRequest, account)
                    : WebCoreManager === null || WebCoreManager === void 0 ? void 0 : WebCoreManager.getTokenSilentlyAsync(tokenRequest));
            });
            const fetchInteractive = () => __awaiter(this, void 0, void 0, function* () {
                return yield (account
                    ? WebCoreManager === null || WebCoreManager === void 0 ? void 0 : WebCoreManager.requestTokenAsync(tokenRequest, account)
                    : WebCoreManager === null || WebCoreManager === void 0 ? void 0 : WebCoreManager.requestTokenAsync(tokenRequest));
            });
            const result = yield (allowUserInteraction
                ? fetchInteractive()
                : fetchSilent());
            if ((result === null || result === void 0 ? void 0 : result.responseStatus) !==
                Windows.Security.Authentication.Web.Core.WebTokenRequestStatus
                    .success) {
                const message = `Failed to get auth token. isResultPresent: ${!!result}, responseStatus: ${result === null || result === void 0 ? void 0 : result.responseStatus}, isResponseDataPresent: ${!!(result === null || result === void 0 ? void 0 : result.responseData)}, responseError: ${JSON.stringify(result === null || result === void 0 ? void 0 : result.responseError)}`;
                logError({
                    eventName: 'HWA_Auth',
                    errorType: 'Request_Failed',
                    message: message,
                });
                console.error(message);
                return Promise.reject(message);
            }
            return result;
        });
    }
}
function isUsersWebAccount(user, account) {
    var _a, _b;
    return user && account && ((_a = account.properties) === null || _a === void 0 ? void 0 : _a['OID']) === ((_b = user.profile) === null || _b === void 0 ? void 0 : _b.oid);
}
export function getExpiry(properties, isCommercial) {
    const expiry = parseInt(properties === null || properties === void 0 ? void 0 : properties.lookup('exp')) * 1000; // Only available on JWT tokens.
    if (expiry || !isCommercial) {
        return expiry;
    }
    return new Date().getTime() + 3600000; // 1 hour in the future
}
function getEffectiveScope(scope) {
    const effectiveScopes = new Set();
    // add the defaults
    effectiveScopes.add('openid');
    effectiveScopes.add('profile');
    if (scope) {
        scope
            .split(' ') // break by space
            .filter((s) => s.length) // remove empty strings
            .forEach((s) => effectiveScopes.add(s)); // add to set
    }
    // join back to string
    return Array.from(effectiveScopes).join(' ');
}
//# sourceMappingURL=WindowsTokens.js.map